<template>

    <div class="quill-container">
        <v-button-toolbar class="quill-options">
            <v-dropdown v-if="variables"
                size="xs"
                icon="fas fa-brackets-curly"
                :label="$t('labels.insert_variable')"
                menu-position="br"
                :items="Object.entries(variables).map(([key, value]) => ({ key, value, icon: 'fas fa-brackets-curly' }))"
                :show-selected="false"
                @change="selectVariable">
            </v-dropdown>
            <v-button size="xs" theme="white" v-if="!editable" icon="fas fa-pencil" @click.native="enableEditor">{{ $t('labels.edit') }}</v-button>
        </v-button-toolbar>
        <div :id="id" class="quill">
            <div :id="`${id}-editor`" :class="`h-${height}`"></div>
        </div>
    </div>

</template>

<style lang="scss">

    // Quill keeps toolbar buttons active based on the last cursor position, even
    // after blur. Reset active styling when the editor is not focused.
    .quill-container:not(:focus-within) {
        .ql-toolbar.ql-snow {
            button.ql-active,
            .ql-picker-label.ql-active {
                color: $color-text;

                .ql-fill {
                    fill: $color-text;
                }

                .ql-stroke {
                    stroke: $color-text;
                }
            }
        }
    }

    .quill-container {
        position: relative;
    }

    .quill {
        &:hover, &:focus {
            .ql-toolbar, .ql-container {
                border-color: $color-rule;
            }
        }
    }

    .ql-container.ql-snow {
        border-bottom-left-radius: 3px;
        border-bottom-right-radius: 3px;
        border: 1px solid $color-rule;
        transition: border-color 0;
        &.ql-disabled .ql-editor {
            background-color: white;
            cursor: not-allowed;
            > * {
                pointer-events: none;
                user-select: none;
            }
        }
    }

    .ql-toolbar.ql-snow {
        border-top-left-radius: 3px;
        border-top-right-radius: 3px;
        border: 1px solid $color-rule;
        padding: 12px;
        transition: border-color 0;
        .ql-picker-label {
            padding: 0 3px;
        }
        svg {
            .ql-fill { fill: $color-text; }
            .ql-stroke { stroke: $color-text; }
            &.ql-active, &:hover {
                .ql-fill { fill: $color-primary; }
                .ql-stroke { stroke: $color-primary; }
            }
        }
    }

    .ql-editor {
        @include font-medium(14px);
        background-color: white;
        color: $color-quill-text;
        font-style: normal;
        line-height: 1.6em;
        padding: 15px;
        p {
            color: $color-quill-text;
            margin: 0 0 0 0;
        }
    }

    .ql-editor.ql-blank::before {
        color: $color-placeholder;
        font-style: normal;
    }

    .quill-options {
        margin: 10px;
        position: absolute;
        right: 0;
        top: 0;
    }

</style>

<script>

import ColorUtil from '@utils/ColorUtil';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

export default {

    props: {
        model: {
            required: true,
        },
        id: {
            type: String,
            default: 'editor',
        },
        height: {
            type: Number,
            default: 250,
        },
        placeholder: {
            type: String,
        },
        variables: {
            type: Object,
        },
        hasColors: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        mode: {
            type: String,
            default: 'format',
            validator: function(value) {
                return ['format', 'highlight', 'none'].includes(value);
            }
        },
    },

    data() {
        return {
            quill: null,
            editable: !this.disabled,
            highlightColors: ColorUtil.highlightColorPresets(),
        }
    },

    mounted() {
        // Set the options.
        let options = [];
        if (this.mode === 'highlight') {
            // Review editor: bold/italic plus highlight colors.
            options.push(['bold', 'italic', { 'background': this.highlightColors }, 'clean']);
        } else if (this.mode === 'format') {
            options.push(['bold', 'italic', 'underline']);
            if (this.hasColors) {
                options.push([{ 'color': [] }, { 'background': [] }]);
            }
            options.push(['link', 'clean']);
        }

        // Initialize the editor.
        this.quill = new Quill(`#${this.id}-editor`, {
            modules: {
                toolbar: options,
                clipboard: {
                    matchVisual: false,
                },
            },
            readOnly: false,
            theme: 'snow',
            bounds: `#${this.id}`,
            placeholder: this.placeholder,
        });

        // Broadcast changes to parent.
        this.quill.on('text-change', () => {
            if (['format', 'highlight'].includes(this.mode)) {
                this.$emit('input', this.quill.root.innerHTML);
            } else {
                this.$emit('input', this.quill.getText());
            }
        });

        // Quill leaves toolbar buttons marked active after blur; clear on focus loss.
        this.quill.on('selection-change', (range) => {
            if (!range) {
                this.clearToolbarActiveStates();
            }
        });

        // Set the inital text.
        if (this.model) {
            let formatted = this.model.replace(/\r?\n/g, '<br>');
            this.quill.clipboard.dangerouslyPasteHTML(formatted, 'silent');
        }

        // Enable the editor.
        this.quill.enable(!this.disabled);

        // Initial content can leave the cursor at index 0 and activate toolbar
        // buttons (e.g. Bold) before the user interacts with the editor.
        this.$nextTick(() => {
            this.quill.setSelection(null);
            this.clearToolbarActiveStates();
        });

        // Remove formatting when pasting.
        const Delta = Quill.import('delta');
        this.quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
            return new Delta().insert(delta.ops.map(op => op.insert).join(''));
        });
    },

    methods: {

        enableEditor() {
            this.editable = true;
            this.quill.enable(true);
            if (!this.quill.hasFocus()) {
                this.quill.focus();
            }
        },

        // Remove stale ql-active classes Quill leaves on toolbar buttons.
        clearToolbarActiveStates() {
            const toolbar = this.quill.container.parentElement?.querySelector('.ql-toolbar');
            if (!toolbar) return;
            toolbar.querySelectorAll('.ql-active').forEach(el => el.classList.remove('ql-active'));
        },

        selectVariable(item) {
            if (!this.quill.hasFocus()) {
                this.quill.focus();
            }
            const index = this.quill.getSelection().index;
            this.quill.insertText(Math.max(0, index), item.key);
        },

    },

}

</script>
